/**
 * Catalog hooks - wraps datasets API for catalog-focused functionality
 */

import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api';
import type { Dataset, PaginatedResponse, CatalogEntry, CatalogEntriesResponse, CatalogEntryDetail } from '@/types';

interface CatalogSearchParams {
  query?: string;
  tags?: string[];
  page?: number;
  limit?: number;
}

export const useCatalogSearch = (params: CatalogSearchParams = {}) => {
  const { query, tags, page = 1, limit = 20 } = params;

  return useQuery<PaginatedResponse<Dataset>>({
    queryKey: ['catalog', 'search', { query, tags, page, limit }],
    queryFn: async () => {
      const searchParams = new URLSearchParams({
        page: page.toString(),
        limit: limit.toString(),
      });

      if (query) {
        searchParams.append('search', query);
      }

      if (tags && tags.length > 0) {
        tags.forEach((tag) => searchParams.append('tags', tag));
      }

      const response = await api.get(`/datasets?${searchParams.toString()}`);
      // API returns { data: [...], pagination: {...}, metadata: {...} }
      // Transform to match frontend expectation: { items: [...], total, page, size, pages }
      // The emitted key is `size` (the PaginatedResponse<T> field, renamed from
      // `limit` in #1473); the local `limit` below is this hook's own page-size
      // request param and keeps its name.
      return {
        items: response.data.data || [],
        total: response.data.pagination?.total || 0,
        page: response.data.pagination?.page || 1,
        size: response.data.pagination?.size || limit,
        pages: response.data.pagination?.pages || 0,
      };
    },
    enabled: true,
  });
};

export const useCatalogAsset = (id: string) => {
  return useQuery<Dataset>({
    queryKey: ['catalog', 'asset', id],
    queryFn: async () => {
      const response = await api.get(`/datasets/${id}`);
      return response.data;
    },
    enabled: !!id,
  });
};

// Get all unique tags from datasets
export const useCatalogTags = () => {
  return useQuery<string[]>({
    queryKey: ['catalog', 'tags'],
    queryFn: async () => {
      // Get datasets and extract unique tags
      const response = await api.get('/datasets?limit=1000');
      const datasets: Dataset[] = response.data.items || [];
      const tagsSet = new Set<string>();

      datasets.forEach((dataset) => {
        dataset.tags?.forEach((tag) => tagsSet.add(tag));
      });

      return Array.from(tagsSet).sort();
    },
  });
};

// ============================================================================
// DuckLake Catalog Entries
// ============================================================================

interface CatalogEntriesParams {
  search?: string;
  schema_name?: string;
  page?: number;
  limit?: number;
}

/**
 * Fetch DuckLake catalog entries (tables)
 */
export const useCatalogEntries = (params: CatalogEntriesParams = {}) => {
  const { search, schema_name, page = 1, limit = 20 } = params;

  return useQuery<CatalogEntriesResponse>({
    queryKey: ['catalog', 'entries', { search, schema_name, page, limit }],
    queryFn: async () => {
      const searchParams = new URLSearchParams({
        page: page.toString(),
        size: limit.toString(),
      });

      if (search) {
        searchParams.append('search', search);
      }

      if (schema_name) {
        searchParams.append('schema_name', schema_name);
      }

      const response = await api.get(`/catalog/entries?${searchParams.toString()}`);
      return response.data;
    },
  });
};

/**
 * Fetch a single catalog entry with full details including columns
 */
export const useCatalogEntry = (entryId: string) => {
  return useQuery<CatalogEntryDetail>({
    queryKey: ['catalog', 'entries', entryId],
    queryFn: async () => {
      const response = await api.get(`/catalog/entries/${encodeURIComponent(entryId)}`);
      return response.data;
    },
    enabled: !!entryId,
  });
};

/**
 * Get unique schema names from catalog entries
 */
export const useCatalogSchemas = () => {
  return useQuery<string[]>({
    queryKey: ['catalog', 'schemas'],
    queryFn: async () => {
      // Get all entries and extract unique schema names
      const response = await api.get('/catalog/entries?size=1000');
      const entries: CatalogEntry[] = response.data.items || [];
      const schemasSet = new Set<string>();

      entries.forEach((entry) => {
        if (entry.schema_name) {
          schemasSet.add(entry.schema_name);
        }
      });

      return Array.from(schemasSet).sort();
    },
  });
};
